import React from 'react'
import { useIntl } from 'react-intl'
import { Modal, Button, Divider } from 'antd'
import { GoogleOutlined, WindowsFilled } from '@ant-design/icons'

import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import IntlMessages from '../../../../util/IntlMessages'
import { FrontendUrls } from '../../../../types/urls'
import { ConnectPlatform } from '../types'

export interface ISignupWithAnotherPlatformModalProps {
  domainPlatform: string
  connectWithPlatform: (platform: string) => void
  handleSignupWithMail: () => void
  handleSigninWithPlatform: (platform: ConnectPlatform) => void
  handleClose: () => void
}

/**
 * The new sign-up page's copy of the shared modal, so the old page keeps its own unchanged. It adds
 * the one thing a visitor whose team may already be here needs to hear before choosing.
 */
const SignupWithAnotherPlatformModal = ({
  domainPlatform,
  connectWithPlatform,
  handleSignupWithMail,
  handleSigninWithPlatform,
  handleClose,
}: ISignupWithAnotherPlatformModalProps): React.ReactElement => {
  const { formatMessage } = useIntl()

  // A plain anchor, like the Knowledge Base link above it: the page's styles restyle antd
  // Typography inside this modal, and a Typography link rendered as plain text there.
  const signinLink = (signinPlatform: ConnectPlatform) =>
    function SigninLink(chunks: React.ReactNode[]) {
      return (
        <a
          href={`${FrontendUrls.signin}?platform=${signinPlatform}`}
          onClick={(event) => {
            event.preventDefault()
            handleSigninWithPlatform(signinPlatform)
          }}
          data-qa={`signup-signin-with-${signinPlatform}-link`}
        >
          {chunks}
        </a>
      )
    }

  let platform
  let importUsersPlatform
  if (domainPlatform === 'google') {
    platform = 'Google Workspace'
    importUsersPlatform = 'Google Workspace'
  }
  if (domainPlatform === 'microsoft') {
    platform = 'Microsoft Office 365'
    importUsersPlatform = 'Microsoft Active Directory'
  }

  return (
    <Modal
      title={formatMessage({ id: 'components.signupWithAnotherPlatform.title' }, { platform })}
      open
      onCancel={handleClose}
      footer="" //Need to remove default buttons
      width={560}
      className="signup-with-another-platform-modal"
    >
      <p>
        <IntlMessages
          id="components.signupWithAnotherPlatform.info"
          values={{
            importUsersPlatform,
            platform,
            // eslint-disable-next-line
            helpDesk: (...chunks) => (
              <a href="https://vacationtracker.io/knowledge-base/" target="_blank" rel="noopener noreferrer">
                {chunks}
              </a>
            ),
          }}
        />
      </p>
      {/* Says "if" on purpose: the address alone cannot tell us whether the team is here, and
          saying so would tell anyone who our customers are. Names every platform rather than the
          mail host's, because a team on Outlook mail can use Vacation Tracker through Slack. */}
      <p>
        <IntlMessages
          id="signup.anotherPlatformModal.joinTeamHint"
          values={{
            slack: signinLink(PlatformEnum.Slack),
            microsoft: signinLink(PlatformEnum.Microsoft),
            google: signinLink(PlatformEnum.Google),
          }}
        />
      </p>
      <p>
        <Button
          type="primary"
          block
          size="large"
          icon={domainPlatform === 'microsoft' ? <WindowsFilled /> : <GoogleOutlined />}
          onClick={() => {
            handleClose()
            connectWithPlatform(domainPlatform)
          }}
          data-qa="signup-with-domain-platform"
        >
          <IntlMessages id="components.signupWithAnotherPlatform.connectWith" values={{ platform }} />
        </Button>
      </p>

      <Divider plain>
        <IntlMessages id="app.or" />
      </Divider>

      <Button key="cancel" type="link" size="large" data-qa="go-to-email-signup" block onClick={handleSignupWithMail}>
        <IntlMessages id="connect.signUp" />
      </Button>
    </Modal>
  )
}

export default SignupWithAnotherPlatformModal
